import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload, CheckCircle2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { recordUpload, canUpload } from "@/hooks/useUploadTracking";

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function StaffSubmission() {
  const { ptaId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  
  // PTA info state
  const [ptaInfo, setPtaInfo] = useState<{ name: string; logo_url: string | null } | null>(null);
  const [loadingPta, setLoadingPta] = useState(true);
  
  // Captcha state
  const [captchaNum1] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [captchaNum2] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  useEffect(() => {
    if (ptaId) {
      fetchPtaInfo();
    }
  }, [ptaId]);

  const fetchPtaInfo = async () => {
    setLoadingPta(true);
    
    // Use the RPC function to fetch PTA by short code (first 8 chars)
    const { data, error } = await supabase
      .rpc('get_pta_by_short_code', { short_code: ptaId });
    
    if (!error && data && data.length > 0) {
      setPtaInfo({
        name: data[0].name,
        logo_url: data[0].logo_url
      });
    }
    
    setLoadingPta(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      toast({ 
        title: "Invalid file type", 
        description: "Only PNG, JPG, and PDF files are allowed", 
        variant: "destructive" 
      });
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast({ 
        title: "File too large", 
        description: "Maximum file size is 10MB", 
        variant: "destructive" 
      });
      return;
    }

    // Check rate limit
    const canProceed = await canUpload();
    if (!canProceed) {
      toast({
        title: "Upload limit reached",
        description: "You can only upload 30 files per 24 hours. Please try again later.",
        variant: "destructive"
      });
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate captcha
    const correctAnswer = captchaNum1 + captchaNum2;
    if (parseInt(captchaAnswer) !== correctAnswer) {
      toast({
        title: "Incorrect Answer",
        description: "Please solve the math question correctly to verify you're human.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    let fileUrl = null;

    // Get full PTA ID from the short code
    const { data: ptaData } = await supabase
      .rpc('get_pta_by_short_code', { short_code: ptaId });
    
    if (!ptaData || ptaData.length === 0) {
      toast({ title: "Error", description: "PTA not found", variant: "destructive" });
      setLoading(false);
      return;
    }

    const fullPtaId = ptaData[0].id;

    // Upload file if provided
    if (file) {
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('receipts')
        .upload(filePath, file);

      if (!uploadError) {
        const { data } = supabase.storage.from('receipts').getPublicUrl(filePath);
        fileUrl = data.publicUrl;
        
        // Record the upload for rate limiting
        await recordUpload('receipts', filePath);
      }
    }

    const { error } = await supabase
      .from('staff_submissions')
      .insert({
        pta_id: fullPtaId,
        first_name: formData.get('first_name') as string,
        last_name: formData.get('last_name') as string,
        role: formData.get('role') as string,
        email: formData.get('email') as string,
        title: formData.get('title') as string,
        details: formData.get('details') as string,
        estimated_cost: formData.get('estimated_cost') as string,
        file_url: fileUrl,
      });

    setLoading(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSubmitted(true);
    }
  };

  if (loadingPta) {
    return (
      <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </main>
    );
  }

  if (!ptaInfo) {
    return (
      <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <p className="text-muted-foreground">PTA not found or this link is invalid.</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6 space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold">Request Submitted!</h2>
            <p className="text-muted-foreground">
              Thank you for your funding request. The PTA committee will review it and get back to you.
            </p>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-card">
        <CardHeader className="text-center">
          {ptaInfo.logo_url && (
            <img 
              src={ptaInfo.logo_url} 
              alt={`${ptaInfo.name} logo`}
              className="h-20 w-auto mx-auto mb-4 object-contain"
            />
          )}
          <CardTitle className="text-2xl">School Staff Request Form</CardTitle>
          <p className="text-sm text-muted-foreground">
            Submit a funding request to {ptaInfo.name}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input id="first_name" name="first_name" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input id="last_name" name="last_name" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Input id="role" name="role" placeholder="e.g., Teacher, Head of Year" required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input id="email" name="email" type="email" placeholder="your.email@school.edu" required />
              <p className="text-xs text-muted-foreground">We'll notify you about the status of your request</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Request Title *</Label>
              <Input id="title" name="title" placeholder="Brief title for your request" required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="details">Details *</Label>
              <Textarea 
                id="details" 
                name="details" 
                placeholder="Provide detailed information about your request"
                rows={5}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_cost">Estimated Cost</Label>
              <Input 
                id="estimated_cost" 
                name="estimated_cost" 
                placeholder="e.g., £500-£750"
              />
            </div>

            <div className="space-y-2">
              <Label>Upload Quote (Optional)</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop or click to upload
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  PNG, JPG, PDF up to 10MB
                </p>
                <Input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={handleFileChange}
                  className="max-w-xs mx-auto"
                />
              </div>
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {/* Captcha */}
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <Label htmlFor="captcha">
                Please solve: {captchaNum1} + {captchaNum2} = ?
              </Label>
              <Input
                id="captcha"
                type="number"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="Your answer"
                required
                className="max-w-[150px]"
              />
              <p className="text-xs text-muted-foreground">
                This helps us prevent spam submissions
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
